import { Group } from '@visx/group'
import type { HierarchyPointLink } from '@visx/hierarchy'
import { LinkHorizontalStep, Polygon } from '@visx/shape'

import { Rhombus } from '../shapes/Rhombus'
import type { Association, Cardinality } from './types'

export const RHOMBUS_LONG_DIAGONAL_LENGTH = 12

const RHOMBUS_SHORT_DIAGONAL_LENGTH = 6
const TRIANGLE_RADIUS = 6

type Offset = {
  dy: number
  dx: number
}

type Props<T> = {
  /** Link item */
  link: HierarchyPointLink<T>
  /** Connector configuration */
  cardinality: Cardinality
  /** Connector configuration */
  association: Association
  /** Offset applied to the source connector */
  sourceConnectorOffset?: Offset
  /** Offset applied to the source connector */
  targetConnectorOffset?: Offset
}

/**
 * Renders a HorizontalStep line from Visx, including connectors that are relevant to the configuration portal diagrams.
 */
export const CompositionStepWithConnectors = <T,>({
  link: originalLink,
  cardinality,
  association,
  sourceConnectorOffset,
  targetConnectorOffset,
}: Props<T>) => {
  const link = {
    source: {
      x: originalLink.source.x + (sourceConnectorOffset?.dy || 0),
      y: originalLink.source.y + (sourceConnectorOffset?.dx || 0),
    },
    target: {
      x: originalLink.target.x + (targetConnectorOffset?.dy || 0),
      y: originalLink.target.y + (targetConnectorOffset?.dx || 0),
    },
  }

  return (
    <Group>
      <LinkHorizontalStep
        data={link}
        className="fill-transparent stroke-modelLine stroke-1"
        percent={0}
      />
      {association === 'composition' && (
        <>
          <Rhombus
            shortAxis={RHOMBUS_SHORT_DIAGONAL_LENGTH}
            longAxis={RHOMBUS_LONG_DIAGONAL_LENGTH}
            orientation="vertical"
            className="fill-modelLine stroke-modelLine stroke-1"
            center={{
              x: link.source.y,
              y: link.source.x + RHOMBUS_LONG_DIAGONAL_LENGTH / 2,
            }}
          />
          <Polygon
            sides={3}
            className="fill-modelLine stroke-modelLine stroke-1"
            size={TRIANGLE_RADIUS}
            center={{
              x: link.target.y - TRIANGLE_RADIUS,
              y: link.target.x,
            }}
          />
        </>
      )}
      {association === 'subtype' && (
        <Polygon
          sides={3}
          className="fill-amaze-paper stroke-modelLine stroke-1 dark:fill-amaze-paperDark"
          size={TRIANGLE_RADIUS}
          rotate={90}
          center={{
            x: link.source.y,
            y: link.source.x + RHOMBUS_LONG_DIAGONAL_LENGTH / 2,
          }}
        />
      )}
      {cardinality === 'many' && (
        <text
          className="stroke-modelLine"
          y={link.target.x}
          x={link.target.y - TRIANGLE_RADIUS}
          dx="-1em"
        >
          *
        </text>
      )}
    </Group>
  )
}
